/**
 * AudioWaveform - Canvas-based real-time audio visualizer.
 *
 * Draws animated frequency bars directly to a canvas element using
 * requestAnimationFrame + Web Audio AnalyserNode. Zero React state
 * updates in the animation loop for smooth 60fps rendering.
 *
 * @module AudioWaveform
 */

import { useRef, useEffect, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AudioWaveformProps {
  /** Active MediaStream from getUserMedia. Pass null when not recording. */
  stream: MediaStream | null;
  /** Canvas width in CSS pixels. */
  width?: number;
  /** Canvas height in CSS pixels. */
  height?: number;
  /** Width of each bar in pixels. */
  barWidth?: number;
  /** Gap between bars in pixels. */
  gap?: number;
  /** Bar fill color. */
  barColor?: string;
  /** FFT size (power of 2). Lower = fewer chunkier bars. */
  fftSize?: number;
  /** Smoothing between frames (0-1). Higher = smoother decay. */
  smoothingTimeConstant?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a real-time audio frequency bar visualizer on a canvas.
 *
 * @param props - Stream and visual configuration
 * @returns A canvas element sized to the given dimensions
 */
export function AudioWaveform({
  stream,
  width = 200,
  height = 48,
  barWidth = 3,
  gap = 2,
  barColor = 'hsl(0, 84%, 60%)',
  fftSize = 256,
  smoothingTimeConstant = 0.7,
}: AudioWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const rafRef = useRef<number>(0);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  const drawBars = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;
    if (!canvas || !analyser || !dataArray) return;

    analyser.getByteFrequencyData(dataArray);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const totalBarWidth = barWidth + gap;
    const barCount = Math.floor(w / totalBarWidth);
    const step = Math.max(1, Math.floor(dataArray.length / barCount));
    const centerY = h / 2;

    ctx.fillStyle = barColor;

    for (let i = 0; i < barCount; i++) {
      // Average frequency data for this bar
      let sum = 0;
      for (let j = 0; j < step; j++) {
        const idx = i * step + j;
        if (idx < dataArray.length) sum += dataArray[idx];
      }
      const avg = sum / step;

      // Normalize to canvas height with a minimum so bars are always visible
      const barHeight = Math.max(2, (avg / 255) * h);
      const x = i * totalBarWidth;
      const y = centerY - barHeight / 2;

      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
        ctx.fill();
      } else {
        ctx.fillRect(x, y, barWidth, barHeight);
      }
    }

    rafRef.current = requestAnimationFrame(drawBars);
  }, [barWidth, gap, barColor]);

  // Set up canvas pixel density for retina displays
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    ctx?.scale(dpr, dpr);
  }, [width, height]);

  // Connect to stream and start animation loop
  useEffect(() => {
    if (!stream) {
      // Draw idle state: subtle flat bars
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const totalBarWidth = barWidth + gap;
      const barCount = Math.floor(w / totalBarWidth);
      const centerY = h / 2;

      ctx.fillStyle = barColor;
      ctx.globalAlpha = 0.25;
      for (let i = 0; i < barCount; i++) {
        const x = i * totalBarWidth;
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(x, centerY - 1, barWidth, 2, barWidth / 2);
          ctx.fill();
        } else {
          ctx.fillRect(x, centerY - 1, barWidth, 2);
        }
      }
      ctx.globalAlpha = 1;
      return;
    }

    // Set up Web Audio API pipeline
    const audioCtx = new AudioContext();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = fftSize;
    analyser.smoothingTimeConstant = smoothingTimeConstant;
    analyser.minDecibels = -90;
    analyser.maxDecibels = -10;

    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);
    // Don't connect to destination - we don't want to hear the mic

    contextRef.current = audioCtx;
    analyserRef.current = analyser;
    sourceRef.current = source;
    dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

    // Start animation
    rafRef.current = requestAnimationFrame(drawBars);

    return () => {
      cancelAnimationFrame(rafRef.current);
      source.disconnect();
      analyser.disconnect();
      if (audioCtx.state !== 'closed') {
        audioCtx.close().catch(() => {});
      }
      analyserRef.current = null;
      sourceRef.current = null;
      contextRef.current = null;
      dataArrayRef.current = null;
    };
  }, [stream, fftSize, smoothingTimeConstant, drawBars, barWidth, gap, barColor]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height }}
      aria-hidden="true"
    />
  );
}
